import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useState } from 'react'
import { Bell, CheckCheck } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { ActivityIcon } from '@/components/dashboard/activity-icon'
import { useDashboard } from '@/components/dashboard/dashboard-provider'

import {
    Popover,
    PopoverContent,
    PopoverTitle,
    PopoverTrigger
} from '@/components/ui/popover'

export const NotificationsMenu: FC = (): ReactNode => {
    const { notifications, unreadCount, markAsRead, markAllAsRead } = useDashboard()
    const [open, setOpen] = useState(false)

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger
                render={(
                    <Button
                        variant='ghost'
                        size='icon-lg'
                        className='relative'
                        aria-label={unreadCount > 0 ? `Notifikasi, ${unreadCount} belum dibaca` : 'Notifikasi'}
                    />
                )}
            >
                <Bell className='size-[18px]' />

                {unreadCount > 0 && (
                    <span className='absolute top-1 right-1 flex min-w-4 items-center justify-center rounded-full bg-brand px-1 text-[10px] leading-4 font-semibold text-brand-foreground ring-2 ring-background tabular-nums'>
                        {unreadCount}
                    </span>
                )}
            </PopoverTrigger>

            <PopoverContent align='end' sideOffset={8} className='w-[min(380px,calc(100vw-1.5rem))] gap-0 p-0'>
                <div className='flex items-center justify-between gap-3 border-b px-4 py-3'>
                    <PopoverTitle className='text-sm font-semibold'>
                        Notifikasi
                        {unreadCount > 0 && (
                            <span className='ml-2 rounded-full bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground tabular-nums'>
                                {unreadCount} baru
                            </span>
                        )}
                    </PopoverTitle>

                    <Button
                        variant='ghost'
                        size='sm'
                        onClick={markAllAsRead}
                        disabled={unreadCount === 0}
                        className='text-muted-foreground'
                    >
                        <CheckCheck />
                        Tandai dibaca
                    </Button>
                </div>

                <ul className='max-h-[min(420px,60vh)] overflow-y-auto py-1'>
                    {notifications.map((item) => (
                        <li key={item.id}>
                            <Link
                                href={item.href}
                                onClick={() => {
                                    markAsRead(item.id)
                                    setOpen(false)
                                }}
                                className={cn(
                                    'flex gap-3 px-4 py-3 transition-colors outline-none hover:bg-muted/70 focus-visible:bg-muted/70',
                                    !item.read && 'bg-brand/[0.04]'
                                )}
                            >
                                <ActivityIcon type={item.type} />

                                <span className='min-w-0 flex-1'>
                                    <span className='flex items-center gap-2'>
                                        <span className='truncate text-sm font-medium text-foreground'>
                                            {item.title}
                                        </span>

                                        {!item.read && (
                                            <>
                                                <span className='size-1.5 shrink-0 rounded-full bg-brand' aria-hidden='true' />
                                                <span className='sr-only'>(belum dibaca)</span>
                                            </>
                                        )}
                                    </span>

                                    <span className='mt-0.5 block text-xs leading-relaxed text-muted-foreground'>
                                        {item.description}
                                    </span>

                                    <span className='mt-1 block text-[11px] text-muted-foreground/80'>
                                        {item.time}
                                    </span>
                                </span>
                            </Link>
                        </li>
                    ))}
                </ul>

                <div className='border-t p-2'>
                    <Link
                        href='/#aktivitas'
                        onClick={() => setOpen(false)}
                        className='flex h-8 items-center justify-center rounded-md text-sm font-medium text-foreground transition-colors outline-none hover:bg-muted focus-visible:bg-muted'
                    >
                        Lihat semua aktivitas
                    </Link>
                </div>
            </PopoverContent>
        </Popover>
    )
}
